<template>
    <div class="case-page page-padding">
        <div class="top-image-form">
            <h1>
                {{ $t('f_top_image_r2') }}
            </h1>
        </div>

        <div class="r2s0form">
            <div ref="formBlock" class="left-right-wrap">
                <div class="left">
                    <div class="own-input">
                        <label for="name">{{ $t('f_name_label') }}</label>
                        <info-tip :text="$t('f_name_tip_text')" />
                        <input
                            id="name"
                            v-model="name"
                            v-validate="'name'"
                            v-m-android-focus
                            name="name"
                            :placeholder="$t('f_name_placeholder')"
                            type="text">
                        <span class="error-message">{{ errors.first(`name`) }}</span>
                    </div>

                    <div class="own-input">
                        <label for="address">{{ $t('f_address_label') }}</label>
                        <info-tip :text="$t('f_address_tip_text')" />
                        <input
                            id="address"
                            v-model="address"
                            v-validate="'required'"
                            v-m-android-focus
                            name="address"
                            :placeholder="$t('f_address_placeholder')"
                            type="text">
                        <span class="error-message">{{ errors.first(`address`) }}</span>
                    </div>

                    <div class="inline-input">
                        <div class="own-input zip-code">
                            <label for="zip_code">{{ $t('f_zip_code_label') }}</label>
                            <input
                                id="zip_code"
                                v-model="zip_code"
                                v-validate="'required'"
                                v-m-android-focus
                                name="zip_code"
                                :placeholder="$t('f_zip_code_placeholder')"
                                type="text">
                            <span class="error-message">{{ errors.first(`zip_code`) }}</span>
                        </div>

                        <div class="own-input">
                            <label for="city">{{ $t('f_city_label') }}</label>
                            <input
                                id="city"
                                v-model="city"
                                v-validate="'required'"
                                v-m-android-focus
                                name="city"
                                :placeholder="$t('f_city_placeholder')"
                                type="text">
                            <span class="error-message">{{ errors.first(`city`) }}</span>
                        </div>
                    </div>

                    <div class="own-input country">
                        <label for="country">{{ $t('f_country_label') }}</label>
                        <info-tip :text="$t('f_country_tip_text')" />
                        <div class="select-wrap">
                            <span class="icon icon-chevron-up" />
                            <select
                                id="country"
                                v-model="country"
                                v-validate="'required'"
                                v-m-android-focus
                                name="country">
                                <option v-for="country in countries" :key="country['code']" :value="country['code']">
                                    {{ country['value'] }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div id="r2s2">
                        <div class="own-input">
                            <label>{{ $t('f_number_of_passenger_label') }}</label>
                            <count-input
                                v-model="people"
                                :decrement="removeLastTraveler"
                                :increment="addTraveler"
                                readonly
                                text="" />
                        </div>

                        <h2 class="title">
                            {{ $t('f_r2s2_title') }}
                        </h2>

                        <r2s2traveler
                            v-for="(traveler, i) of filteredTravelersList"
                            :key="i + 1"
                            :index="i + 1"
                            :traveler="traveler" />

                        <div class="add-traveler" @click="addTraveler">
                            <span class="plus">+</span> <span class="underline">{{ $t('f_add_traveler_btn') }}</span>
                        </div>
                    </div>

                    <div class="own-input next-prev-wrap">
                        <div :class="['btn btn-orange', { 'invalid': formIsInvalid(), 'progress': loading }]" @click="next">
                            <span v-show="!loading">{{ $t('f_save') }} &#10093;</span>
                            <div v-if="loading" class="spinner">
                                <div class="rect1" />
                                <div class="rect2" />
                                <div class="rect3" />
                                <div class="rect4" />
                                <div class="rect5" />
                            </div>
                        </div>
                        <div v-if="isUpdatedSuccessfully" class="update-success">
                            <img alt="" class="checkmark" src="@images/checkmark.png">
                            <span>{{ $t('f_updated_data_thanks') }}</span>
                        </div>
                    </div>
                </div>

                <div class="right">
                    <right-part-cases
                        :total_clients="$props.total_clients"
                        :average_saved="$props.average_saved"
                        :total_compensation="$props.total_compensation"
                        :reviews="$props.reviews"
                        :score="$props.score" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { createHelpers } from 'vuex-map-fields'
import CountInput from '@components/common/CountInput'
import R2s2traveler from "@components/round2/R2s2traveler"
import RightPartCases from "@components/common/RightPartCases"
import formNavMixin from '@components/mixins/formNavMixin'

const { mapFields } = createHelpers({
  getterType: 'round2/getComplaintField',
  mutationType: 'round2/UPDATE_COMPLAINT_FIELD'
})

const { mapMultiRowFields } = createHelpers({
  getterType: 'travelers/getField',
  mutationType: 'travelers/updateField'
})

export default {
  components: {
    CountInput,
    R2s2traveler,
    RightPartCases
  },
  mixins: [formNavMixin],
  props: ['total_compensation', 'total_clients', 'average_saved', 'reviews', 'score'],
  data() {
    return {
      module: 'round2',
      isUpdatedSuccessfully: false,
      initAddressMissing: false,
    }
  },
  computed: {
      ...mapGetters({
        countries: 'round1/countries'
      }),
    ...mapFields([
      'name',
      'address',
      'zip_code',
      'city',
      'country',
      'terms'
    ]),
    ...mapMultiRowFields({
      travelersList: 'list'
    }),
    locale() {
      return this.$i18n.locale
    },
    people: function () {
      return this.$store.state.travelers.people
    },
    filteredTravelersList: function () {
      return this.travelersList.filter((traveler) => !traveler.for_destroy)
    }
  },
  watch: {
    step: function () {
      this.scrollToFormTop()
    }
  },
  created() {
    // get auth
    let auth_key = window.location.href.split('authorizations/')[1].split('/')[0]
    this.$store.dispatch('round2/getComplaint', auth_key)
      .then(message => {
        console.log(message);
      })
      .catch(error => {
        console.error(error);
      });
    if (!this.address || !this.zip_code || !this.city || !this.country) {
      this.initAddressMissing = true;
    }
  },
  mounted() {
    setTimeout(() => {
      this.scrollToFormTop()
    }, 300)
  },
  methods: {
      ...mapMutations({
          cancelEditing: 'round1/CANCEL_EDITING',
          applyEditing: 'round1/APPLY_EDITING',
          addTraveler: 'travelers/ADD',
          removeTraveler: 'travelers/REMOVE',
      }),
      ...mapActions({
          validation: 'round1/validation'
      }),
      removeLastTraveler: function () {
          let lastItem = this.filteredTravelersList[this.filteredTravelersList.length - 1]
          if (lastItem) { this.removeTraveler(lastItem.id) }
      },
      next() {
          this.formSubmittion({ scope: `*`, formSelector: '.r2s0form' }).then(({ success, fail }) => {
              this.$store.dispatch("round2/putComplaint", this.$store.getters['round2/r2s0FormData']).then(() => {
                  success();
                  if (this.initAddressMissing) {
                    this.loading = true;
                    let raw_auth_key = window.location.href.split('authorizations/').pop()
                    let auth_key = raw_auth_key.split('/').shift() || raw_auth_key
                    window.location.href = window.location.origin + "/authorizations/" + auth_key
                  } else {
                    this.isUpdatedSuccessfully = true;
                  }
              }).catch((response) => {
                  fail(response);
              });
          });
      },
      scrollToFormTop() {
          let top_position = this.$device.mobile() ? 0 : this.$refs.formBlock?.offsetTop
          window.scrollTo({ top: top_position, behavior: 'smooth' })
      }
    },
}
</script>

<style lang="scss">
.update-success {
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f7f7f7;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  font-size: 0.8em;
  width: 100%;
  max-width: calc(100% - 40px);
}

.next-prev-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

img.checkmark {
  width: 20px;
  height: auto;
  margin-right: 5px;
}

.btn.btn-orange {
  width: 100%;
  align-self: center;
}

.r2s0form {
  .own-input {
    .content {
      bottom: 110px;
    }
  }

  .own-input.country {
    .content {
      bottom: 90px;
    }
  }

  .own-input.email {
    .content {
      bottom: 212px;
    }
  }

  .zip-code {
    width: 45%;
  }

  .travelers {
    margin-top: 20px;
    background: #f7f7f7;
    border-radius: 8px;
    padding: 15px;

    .traveler-inputs {
      display: flex;
      align-items: center;
      margin-bottom: 10px;

      input[type="text"],
      input[type="email"] {
        flex: 1;
        margin-right: 10px;
        border: 1px solid #ccc;
        padding: 10px;
        border-radius: 4px;
      }

      button {
        background: #ff6a00;
        color: white;
        border: none;
        padding: 10px 15px;
        border-radius: 4px;
        cursor: pointer;

        &:hover {
          background: darken(#ff6a00, 10%);
        }
      }
    }
  }
}
</style>
